// React Router v6 migration complete
// Unified routing with AppRoutes - replaces individual admin pages
import React, {useCallback, useEffect} from 'react'
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom'
import {updateCurrentUser} from '~/src/store/slices/root-slice'
import PrivateRoute from './components/common/PrivateRoute'
import {Loader} from './components/ui/loader'
import RegistrationPage from './pages/Registration'
import RoleSelectPage from './pages/RoleSelect'
import PageNotFound, {NotAuthorized} from './pages/sign-in/404'
import CreateAccountPortal from './pages/sign-in/CreateAccountPortal'
import CreateAgency from './pages/sign-in/CreateAgency'
import CreateCompany from './pages/sign-in/CreateCompany'
import ForgotPassword from './pages/sign-in/ForgotPassword'
import PasswordConfirmation from './pages/sign-in/PasswordConfirmation'
import PasswordResetPage from './pages/sign-in/PasswordReset'
import RegistrationSuccess from './pages/sign-in/RegistrationSuccess'
import SigninPage from './pages/sign-in/Signin'
import UnauthorizedPage from './pages/UnauthorizedPage'
import {AppRoutes} from './routes/AppRoutes'
import {BASE_ROUTES} from './routes/constants'
import {useLazyGetUserByEmailQuery} from './store/apis/user-api'
import {usePartialUser} from './store/slice-hooks'
import {redirect} from './store/slices/root-slice'
import {useAppDispatch, useAppSelector} from './store/store-hooks'
import {isNullOrUndefined} from './utils/type-validators'

export default function App(): JSX.Element {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {redirectTo, appIsLoaded} = useAppSelector((state) => state.root)
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const {partialUser} = usePartialUser()

  const fetchUserDetails = useCallback(
    async (_partialUser) => {
      const user = await getUserByEmail(_partialUser.emailAddress).unwrap()
      dispatch(updateCurrentUser(user))
    },
    [dispatch, getUserByEmail],
  )

  useEffect(() => {
    if (!isNullOrUndefined(partialUser)) {
      fetchUserDetails(partialUser).finally(() => {})
    } else {
      // If there's no partial user, set app as loaded to allow navigation to signin
      dispatch({type: 'rootSlice/setAppLoaded', payload: true})
    }
  }, [fetchUserDetails, partialUser, dispatch])

  // Cognito session check removed - now using MSP platform JWT tokens

  useEffect(() => {
    if (!isNullOrUndefined(redirectTo)) {
      navigate(redirectTo)
      dispatch(redirect())
    }
  }, [redirectTo, dispatch, navigate])

  if (!appIsLoaded) return <Loader page="mini" size="large" />

  return (
    <Routes>
      {/* Public routes */}
      <Route path={`${BASE_ROUTES.REGISTRATION}/*`} element={<RegistrationPage />} />
      <Route path={`${BASE_ROUTES.CREATE_ACCOUNT_PORTAL}/*`} element={<CreateAccountPortal />} />
      <Route path={BASE_ROUTES.REGISTRATION_SUCCESS} element={<RegistrationSuccess />} />
      <Route path={`${BASE_ROUTES.CREATE_COMPANY}/*`} element={<CreateCompany />} />
      <Route path={`${BASE_ROUTES.CREATE_AGENCY}/*`} element={<CreateAgency />} />
      <Route
        path={`${BASE_ROUTES.FORGOT_PASSWORD}/:email?/:confirmationCode?`}
        element={<ForgotPassword />}
      />
      <Route path={BASE_ROUTES.PASSWORD_RESET} element={<PasswordResetPage />} />
      <Route path={BASE_ROUTES.PASSWORD_CONFIRMATION} element={<PasswordConfirmation />} />
      <Route path={`${BASE_ROUTES.SIGNIN}/:emailAddress?/:token?`} element={<SigninPage />} />

      {/* Role selection page */}
      <Route
        path={`${BASE_ROUTES.ROLE_SELECT}/*`}
        element={
          <PrivateRoute>
            <RoleSelectPage />
          </PrivateRoute>
        }
      />

      {/* Protected routes - Single unified route (variant determined from user role) */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <AppRoutes />
          </PrivateRoute>
        }
      />

      {/* Redirect root to signin */}
      <Route path={BASE_ROUTES.ROOT} element={<Navigate to={BASE_ROUTES.SIGNIN} replace />} />

      {/* Error routes */}
      <Route path={BASE_ROUTES.NOT_AUTHORIZED} element={<NotAuthorized />} />
      <Route path={BASE_ROUTES.UNAUTHORIZED} element={<UnauthorizedPage />} />
      <Route path={BASE_ROUTES.NO_MATCH} element={<PageNotFound />} />
    </Routes>
  )
}
